import { FC, MouseEvent, useState } from "react";
import { Box, Popover, Stack, SxProps } from "@mui/material";
import { IconCheck } from "@/assets/icons";
import { JOB_TAG_COLORS, JOB_TAG_COLOR_MAP, TJobTagColor, TJobTagColorKey } from "@/features/job/constants/job-tag-colors";

type TJobTagColorPickerProps = {
  value: string;
  /** colors already used by other rows (cannot be reused) */
  disabledKeys: string[];
  onChange: (key: TJobTagColorKey) => void;
};

const POPOVER_ANCHOR_ORIGIN = { vertical: "bottom", horizontal: "left" } as const;
const POPOVER_TRANSFORM_ORIGIN = { vertical: "top", horizontal: "left" } as const;
const POPOVER_PAPER_SX: SxProps = { "& .MuiPaper-root": { mt: 0.5, p: 1.25, borderRadius: "8px", boxShadow: "0px 8px 24px 0px #00000026" } };
const PALETTE_GRID_SX: SxProps = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 1, width: 168 };

const getPreviewSx = (color: TJobTagColor): SxProps => ({
  width: 28,
  height: 28,
  borderRadius: "6px",
  border: `2px solid ${color.border}`,
  backgroundColor: color.bg,
  cursor: "pointer",
  flexShrink: 0,
});

const getSwatchSx = (color: TJobTagColor, disabled: boolean): SxProps => ({
  width: 32,
  height: 32,
  borderRadius: "8px",
  border: `2px solid ${color.border}`,
  backgroundColor: color.bg,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.35 : 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

export const JobTagColorPicker: FC<TJobTagColorPickerProps> = ({ value, disabledKeys, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected = JOB_TAG_COLOR_MAP[value] ?? JOB_TAG_COLORS[0];

  const isDisabled = (key: string) => disabledKeys.includes(key) && key !== value;

  const handleOpen = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (key: TJobTagColorKey) => {
    if (isDisabled(key)) return;
    onChange(key);
    setAnchorEl(null);
  };

  return (
    <>
      <Box onClick={handleOpen} sx={getPreviewSx(selected)} />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={POPOVER_ANCHOR_ORIGIN}
        transformOrigin={POPOVER_TRANSFORM_ORIGIN}
        sx={POPOVER_PAPER_SX}
      >
        <Box sx={PALETTE_GRID_SX}>
          {JOB_TAG_COLORS.map((c) => (
            <Stack key={c.key} alignItems="center" justifyContent="center">
              <Box onClick={() => handleSelect(c.key)} sx={getSwatchSx(c, isDisabled(c.key))}>
                {c.key === value && <IconCheck width={18} height={18} color={c.border} />}
              </Box>
            </Stack>
          ))}
        </Box>
      </Popover>
    </>
  );
};
